import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
    Alert,
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Skeleton,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from "@mui/material";
import { CalendarMonth, Download, FilterList, Palette, Share } from "@mui/icons-material";
import Logo from "../../assets/images/floki-the-fox.png";
import UserHeader from "./UserHeader.component"; // Assuming this component exists
import { useBitcoinPrice } from "../../state/context/bitcoin.context"; // Assuming this context exists
import { useUserTokens } from "../../hooks";
import { transformToBTC } from "../../utils/helpers"; // Assuming this utility exists
import { useTranslation } from 'react-i18next';

// ------------------ Constants & Helpers ------------------

const SATS_PER_BTC = 100_000_000_000; // Corrected SATS per BTC
const TOKEN_IMG_URL = (id = "TK") => `https://images.odin.fun/token/${ id }`;

const formatBtc = (n = 0) =>
    Number(n || 0).toLocaleString("en-US", {
        minimumFractionDigits: 4,
        maximumFractionDigits: 6,
    });

const startOfDay = (d) => {
    const date = new Date(d);
    date.setHours(0, 0, 0, 0);
    return date;
};

const endOfDay = (d) => {
    const date = new Date(d);
    date.setHours(23, 59, 59, 999);
    return date;
};

const addDays = (d, n) => {
    const date = new Date(d);
    date.setDate(date.getDate() + n);
    return date;
};

const computeStats = (trades = []) => {
    if (!trades || trades.length === 0) {
        return { netPnL: 0, winRate: 0, totalTrades: 0, topTokens: [], totalInvested: 0, returnOnInvestment: 0 };
    }
    const normalized = trades.map((token) => {
        const totalBuyBTC = (parseInt(token.totalBuyBTCVolume, 10) || 0) / SATS_PER_BTC;
        const totalSellBTC = (parseInt(token.totalSellBTCVolume, 10) || 0) / SATS_PER_BTC;
        const pnl = totalSellBTC - totalBuyBTC;
        return { pnl, isWin: pnl > 0, symbol: token.ticker || "UNKNOWN", totalInvested: totalBuyBTC, tradeCount: token.tradeCount || 0, _id: token._id || token.token || "TK" };
    });
    const totalTrades = normalized.reduce((sum, t) => sum + t.tradeCount, 0);
    const wins = normalized.filter((t) => t.isWin).length;
    const netPnL = normalized.reduce((sum, t) => sum + t.pnl, 0);
    const totalInvested = normalized.reduce((sum, t) => sum + t.totalInvested, 0);
    const winRate = trades.length > 0 ? (wins / trades.length) * 100 : 0;
    const returnOnInvestment = totalInvested > 0 ? (netPnL / totalInvested) * 100 : 0;
    const topTokens = normalized.map(({ symbol, pnl, _id }) => ({ symbol, pnl, id: _id })).sort((a, b) => Math.abs(b.pnl) - Math.abs(a.pnl));
    return { netPnL, winRate, totalTrades, topTokens, totalInvested, returnOnInvestment };
};

// ------------------ Context for State Management ------------------

const PNLCardContext = createContext(null);

export const usePNLCard = () => {
    const context = useContext(PNLCardContext);
    if (!context) {
        throw new Error("usePNLCard must be used within a PNLCardProvider");
    }
    return context;
};

const PNLCardProvider = ({ userId, children }) => {
    const [filters, setFilters] = useState({ tokens: [], start: undefined, end: undefined });
    const [gradientColors, setGradientColors] = useState({ start: "#291e63ff", end: "#5d431fff" });

    const queryParams = useMemo(() => ({
        userId,
        limit: 10,
        ...(filters.tokens.length && { tokens: filters.tokens }),
        ...(filters.start && { start: filters.start.toISOString() }),
        ...(filters.end && { end: filters.end.toISOString() }),
    }), [userId, filters]);

    const { data, loading, error } = useUserTokens(queryParams);

    const title = useMemo(() => {
        if (filters.start || filters.end) {
            const start = filters.start ? filters.start.toISOString().slice(0, 10) : "the beginning";
            const end = filters.end ? filters.end.toISOString().slice(0, 10) : "now";
            return `Performance from ${ start } to ${ end }`;
        }
        return "Overall Performance";
    }, [filters]);

    const value = { userId, filters, setFilters, gradientColors, setGradientColors, data, loading, error, title };
    return <PNLCardContext.Provider value={value}>{children}</PNLCardContext.Provider>;
};

// ------------------ Compact Social Card ------------------

const SocialPnLCardCompact = ({ brand = "FLOKITOOLS.fun", logoUrl = Logo }) => {
    const [isExporting, setIsExporting] = useState(false);
    const nodeRef = useRef(null);
    const { userId, data, title, gradientColors } = usePNLCard();
    const { price: btcPrice } = useBitcoinPrice();
    const { t } = useTranslation();

    useEffect(() => {
        const SCRIPT_ID = "html2canvas-compact";
        if (document.getElementById(SCRIPT_ID)) return;
        const script = document.createElement("script");
        script.id = SCRIPT_ID;
        script.src = "https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js";
        script.async = true;
        document.body.appendChild(script);
    }, []);

    const stats = useMemo(() => computeStats(data?.trades || []), [data]);
    const { netPnL, returnOnInvestment, winRate, totalTrades, topTokens } = stats;
    const pnlPositive = netPnL >= 0;

    const exportPng = async () => {
        if (!nodeRef.current || !window.html2canvas) {
            alert(t('analytics.imageRenderingNotReady'));
            return;
        }
        setIsExporting(true);
        try {
            const canvas = await window.html2canvas(nodeRef.current, {
                allowTaint: true,
                useCORS: true,
                scale: 2, // Higher scale for better resolution
                backgroundColor: null, // Transparent background to capture gradient
            });
            const image = canvas.toDataURL("image/png", 1.0);
            const link = document.createElement("a");
            link.href = image;
            link.download = `flokitools-pnl-card-${ userId }.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (err) {
            console.error("Failed to export PNG:", err);
            alert(t('analytics.exportError'));
        } finally {
            setIsExporting(false);
        }
    };

    const shareOnX = () => {
        const pnlBtc = netPnL.toFixed(4);
        const roi = returnOnInvestment.toFixed(1);
        const text = `${ t('analytics.checkOutMyStats') }
        
${ t('analytics.netPnl') }: ${ pnlBtc } BTC
${ t('analytics.return') }: ${ roi }%

${ t('analytics.generatedWith') } @FlokiTools.`;

        const params = new URLSearchParams({
            text,
            url: "https://flokitools.fun",
            hashtags: "odinfun,flokitools,Bitcoin,Runes,Trading",
        });

        const twitterUrl = `https://twitter.com/intent/tweet?${ params.toString() }`;
        window.open(twitterUrl, "_blank", "width=550,height=420");
    };

    let pnlDisplay = transformToBTC(netPnL * SATS_PER_BTC);
    let pnlSymbol = "BTC";
    if (pnlDisplay.includes("SATS")) pnlSymbol = "SATS";
    pnlDisplay = pnlDisplay.replace(" BTC", "").replace(" SATS", "");

    return (
        <Box sx={{ width: "100%", maxWidth: "1200px" }}>
            <Card ref={nodeRef} sx={{ aspectRatio: "1200 / 675", position: "relative", overflow: "hidden", borderRadius: "24px", color: "#E5E7EB", backgroundImage: `linear-gradient(135deg, ${ gradientColors?.start || "#0B1220" } 0%, ${ gradientColors?.end || "#b86f2cff" } 100%)`, border: "1px solid rgba(255, 255, 255, 0.5)" }}>
                <CardContent sx={{ position: "relative", height: "100%", p: 4, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                    <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 3 }}>
                        <Box sx={{ minWidth: 0 }}><UserHeader userId={userId} usePnlButton={false} /><Typography variant="h2" component="h2" noWrap sx={{ fontSize: "2rem", fontWeight: 800 }}>{title}</Typography><Typography variant="body2" color="#9CA3AF">{t('analytics.autoGeneratedSnapshot')}</Typography></Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0 }}><Avatar src={logoUrl} sx={{ width: 36, height: 36, border: "1px solid rgba(255,255,255,0.1)" }} /><Typography variant="body2" fontWeight={600} color="#9CA3AF">{brand}</Typography></Box>
                    </Box>
                    <Grid container spacing={3} alignItems="flex-end">
                        <Grid item xs={12} md={6}>
                            <Typography
                                variant="subtitle1"
                                color="#9CA3AF"
                                sx={{
                                    textTransform: "uppercase",
                                    letterSpacing: "0.05em",
                                    fontWeight: 800,
                                    fontSize: "1.15rem",
                                    mb: 0.5,
                                }}
                            >
                                {t('analytics.netPnl')} ({pnlSymbol})
                            </Typography>
                            <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1.5 }}>
                                <Typography variant="h1" component="span" sx={{ fontSize: { xs: "3rem", md: "4rem" }, fontWeight: 900, color: pnlPositive ? "#10B981" : "#EF4444", lineHeight: 1 }}>{pnlDisplay}</Typography>
                                {btcPrice && (<Typography component="span" sx={{ fontSize: "1rem", fontWeight: 400, color: "#9CA3AF" }}>(${(netPnL * btcPrice).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })})</Typography>)}
                                <Typography variant="subtitle2" color="#9CA3AF" sx={{ mb: 1.5, ml: 1, fontWeight: 700, fontSize: "1.05rem" }}>{totalTrades} {t('analytics.trades')}</Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={6} md={3}>
                            <Typography
                                variant="subtitle1"
                                color="text.secondary"
                                sx={{
                                    textTransform: "uppercase",
                                    letterSpacing: "0.05em",
                                    fontWeight: 800,
                                    fontSize: "1.15rem",
                                    mb: 0.5,
                                }}
                            >
                                {t('analytics.return')}
                            </Typography>
                            <Typography variant="h2" component="p" sx={{ fontSize: { xs: "2.5rem", md: "3rem" }, fontWeight: 800, color: pnlPositive ? "#10B981" : "#EF4444" }}>{returnOnInvestment.toFixed(1)}%</Typography>
                        </Grid>
                        <Grid item xs={6} md={3}>
                            <Typography
                                variant="subtitle1"
                                color="text.secondary"
                                sx={{
                                    textTransform: "uppercase",
                                    letterSpacing: "0.05em",
                                    fontWeight: 800,
                                    fontSize: "1.15rem",
                                    mb: 0.5,
                                }}
                            >
                                {t('analytics.winRate')}
                            </Typography>
                            <Typography variant="h2" component="p" sx={{ fontSize: { xs: "2.5rem", md: "3rem" }, fontWeight: 800 }}>{winRate.toFixed(1)}%</Typography>
                        </Grid>
                    </Grid>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                            {topTokens.length > 0 ? (
                                topTokens.slice(0, 3).map((t) => (
                                    <Box key={t.id} sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 1, borderRadius: "16px", bgcolor: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)" }}>
                                        <Avatar src={TOKEN_IMG_URL(t.id)} sx={{ width: 24, height: 24 }} />
                                        <Typography variant="body1" fontWeight={700} sx={{ fontSize: "1.1rem" }}>{t.symbol}</Typography>
                                        <Typography variant="subtitle2" sx={{ fontFamily: "monospace", color: t.pnl >= 0 ? "#10B981" : "#EF4444", fontWeight: 700, fontSize: "1rem" }}>
                                            {t.pnl >= 0 ? "+" : ""}{transformToBTC(t.pnl * SATS_PER_BTC)}
                                        </Typography>
                                    </Box>
                                ))
                            ) : (
                                <Typography variant="body2" color="#9CA3AF">{t('analytics.noTokenActivity')}</Typography>
                            )}
                            {
                                topTokens.length > 3 && (
                                    <Typography variant="body2" color="#9CA3AF" sx={{ fontStyle: "italic" }}>+{topTokens.length - 3} {t('analytics.more')}</Typography>
                                )
                            }
                        </Box>
                        <Box>
                            <Typography
                                variant="subtitle1"
                                color="#9CA3AF"
                                align="right"
                                component="div"
                                sx={{
                                    fontWeight: 800,
                                    fontSize: "1.1rem",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.05em",
                                    mb: 0.5,
                                }}
                            >
                                {t('analytics.totalInvested')}
                            </Typography>
                            <Typography variant="h6" fontWeight={700} align="right" sx={{
                                whiteSpace: "nowrap"
                            }}>
                                {transformToBTC(stats.totalInvested * SATS_PER_BTC)}
                                {btcPrice && (
                                    <Typography component="span" sx={{ fontSize: "0.95rem", fontWeight: 600, color: "#9CA3AF", ml: 1 }}>
                                        (${(stats.totalInvested * btcPrice).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })})
                                    </Typography>
                                )}
                            </Typography>
                        </Box>
                    </Box>
                </CardContent>
            </Card>
            <Box sx={{ mt: 2, display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 1.5, justifyContent: "flex-end" }}>
                <Button onClick={exportPng} disabled={isExporting} variant="contained" startIcon={isExporting ? <CircularProgress size={20} color="inherit" /> : <Download />} sx={{ bgcolor: "#7C4DFF", "&:hover": { bgcolor: "#6B3DFF" } }}>{isExporting ? t('analytics.rendering') : t('analytics.exportPng')}</Button>
                <Button onClick={shareOnX} variant="contained" startIcon={<Share />} sx={{ bgcolor: "#1DA1F2", "&:hover": { bgcolor: "#0c85d0" } }}>{t('analytics.quickTweet')}</Button>
            </Box>
        </Box>
    );
};

// ------------------ Sidebar Filters ------------------

const SidebarFilters = () => {
    const [preset, setPreset] = useState("7d");
    const [selectedTokens, setSelectedTokens] = useState([]);
    const [customStart, setCustomStart] = useState("");
    const [customEnd, setCustomEnd] = useState("");
    const { data, setFilters, gradientColors, setGradientColors } = usePNLCard();
    const { t } = useTranslation();

    const handleApply = () => {
        const now = new Date();
        let start, end;
        switch (preset) {
            case "24h":
                start = addDays(now, -1);
                end = now;
                break;
            case "7d":
                start = addDays(now, -7);
                end = now;
                break;
            case "30d":
                start = addDays(now, -30);
                end = now;
                break;
            case "custom":
                start = customStart ? startOfDay(new Date(customStart)) : addDays(now, -7);
                end = customEnd ? endOfDay(new Date(customEnd)) : now;
                break;
            default:
                start = addDays(now, -7);
                end = now;
        }
        setFilters({ tokens: selectedTokens, start, end });
    };

    const handleTokenChange = (event) => {
        const { value } = event.target;
        setSelectedTokens(typeof value === "string" ? value.split(",") : value);
    };

    const currentColors = { start: gradientColors?.start ?? "#e48c10ff", end: gradientColors?.end ?? "#a22b45ff" };

    return (
        <Card component="aside" sx={{ p: 2, height: "max-content", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "16px", bgcolor: "rgba(255,255,255,0.05)" }}>
            <Typography variant="h6" component="h3" sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}><FilterList /> {t('analytics.filters')}</Typography>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <FormControl fullWidth>
                        <InputLabel id="tokens-select-label">{t('analytics.tokens')}</InputLabel>
                        <Select
                            labelId="tokens-select-label"
                            multiple
                            value={selectedTokens}
                            label={t('analytics.tokens')}
                            onChange={handleTokenChange}
                            renderValue={(selected) => {
                                if (selected.length === 0) return t('analytics.all');
                                const selectedNames = (data?.tokens || []).filter((t) => selected.includes(t.id)).map((t) => t.ticker || t.name);
                                return selectedNames.join(", ");
                            }}
                        >
                            {(data?.tokens || []).map((token) => (
                                <MenuItem key={token.id} value={token.id} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Avatar src={TOKEN_IMG_URL(token.id)} sx={{ width: 24, height: 24 }} />{token.ticker || token.name || "TKN"}</Box>
                                    <input type="checkbox" readOnly checked={selectedTokens.includes(token.id)} style={{ pointerEvents: "none" }} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>{t('analytics.timeRange')}</Typography>
                    <ToggleButtonGroup value={preset} exclusive onChange={(e, v) => v && setPreset(v)} fullWidth>
                        {["24h", "7d", "30d", "custom"].map((p) => (<ToggleButton key={p} value={p}>{p.toUpperCase()}</ToggleButton>))}
                    </ToggleButtonGroup>
                </Grid>
                {preset === "custom" && (<><Grid item xs={6}><TextField type="date" label={t('analytics.start')} value={customStart} onChange={(e) => setCustomStart(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth /></Grid><Grid item xs={6}><TextField type="date" label={t('analytics.end')} value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth /></Grid></>)}
                <Grid item xs={12}><Button onClick={handleApply} variant="contained" fullWidth startIcon={<CalendarMonth />} sx={{ bgcolor: "#7C4DFF", "&:hover": { bgcolor: "#6B3DFF" } }}>{t('analytics.applyFilters')}</Button></Grid>
                <Grid item xs={12}><Typography variant="body2" color="text.secondary" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}><Palette fontSize="small" /> {t('analytics.cardGradientColors')}</Typography></Grid>
                <Grid item xs={6}>
                    <Typography variant="caption">{t('analytics.start')}</Typography>
                    <TextField type="color" value={currentColors.start.slice(0, 7)} onChange={(e) => setGradientColors({ ...currentColors, start: e.target.value })} fullWidth sx={{ "& input[type='color']": { p: 0.5, height: "40px", cursor: "pointer" } }} />
                </Grid>
                <Grid item xs={6}>
                    <Typography variant="caption">{t('analytics.end')}</Typography>
                    <TextField type="color" value={currentColors.end.slice(0, 7)} onChange={(e) => setGradientColors({ ...currentColors, end: e.target.value })} fullWidth sx={{ "& input[type='color']": { p: 0.5, height: "40px", cursor: "pointer" } }} />
                </Grid>
            </Grid>
        </Card>
    );
};

// ------------------ Page Wrapper ------------------

const SocialPnLWithSidebar = ({ brand }) => {
    const { loading, error } = usePNLCard();
    const { t } = useTranslation();

    const renderContent = () => {
        if (loading) return <Skeleton variant="rectangular" sx={{ borderRadius: '24px', aspectRatio: "1200 / 675" }} />;
        if (error) return <Alert severity="error">{t('analytics.failedToLoadTrades')}: {error.message || t('analytics.unknownError')}</Alert>;
        return <SocialPnLCardCompact brand={brand} />;
    };
    return (
        <Box sx={{ p: { xs: 2, md: 4 }, width: "100%", color: "#E5E7EB", fontFamily: "Inter, sans-serif" }}>
            <Grid container spacing={3}>
                <Grid item xs={12} xl={3}><SidebarFilters /></Grid>
                <Grid item xs={12} xl={9}>{renderContent()}</Grid>
            </Grid>
        </Box>
    );
};

// ------------------ Dialog Entry Point ------------------

const PNLCard = ({ open, onClose, userId }) => {
    const { t } = useTranslation();

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth PaperProps={{ sx: { bgcolor: "black", border: "1px solid rgba(255,255,255,0.1)" } }}>
            <DialogTitle sx={{ color: "#E5E7EB", borderBottom: "1px solid rgba(255,255,255,0.1)", textAlign: "center", fontSize: "1.5rem", fontWeight: 800 }}>{t('analytics.pnlCardGenerator')}</DialogTitle>
            <DialogContent sx={{}}>
                <PNLCardProvider userId={userId}>
                    <SocialPnLWithSidebar brand="FLOKITOOLS.fun" />
                </PNLCardProvider>
            </DialogContent>
        </Dialog>
    );
};

export default PNLCard;